import moment from 'moment';
import { Row, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ArchiveJobModal from 'views/manifests/components/ArchiveJobModal';
import UnarchiveJobModal from 'views/manifests/components/UnarchiveJobModal';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { JobResponse } from 'store/jobs/job';
import { archiveJob, unarchiveJob } from 'store/jobs/client';
import Notification from 'components/ui/base/notification/Notification';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import DatePicker from 'components/new-ui/base/date-picker/DatePicker';
import JobsTable from 'views/jobs/components/JobsTable';
import AssignJobModal from 'views/manifests/components/AssignJobModal';
import { usePartyContext } from '../../PartyProvider';
import { useJobsFilters } from 'views/jobs/useJobsFiters';
import JobFiltersModal from 'views/jobs/components/JobFiltersModal';
import Button from 'components/new-ui/base/button/Button';
import AppliedJobFilters from 'views/jobs/components/AppliedJobFilters';
import ArchiveFilter from 'components/new-ui/composed/archive-filter/ArchiveFilter';
import useIndividuals from 'hooks/useIndividuals';
import EditJobModal from 'views/jobs/components/edit-job/EditJobModal';
import useCustomers from 'hooks/useCustomers';

const JobsTab = () => {
    const navigate = useNavigate();
    const { customerId, viewOnly } = usePartyContext();
    const { listIndividuals } = useIndividuals();
    const { getJobsCount } = useCustomers();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();

    const [jobIdToArchive, setJobIdToArchive] = useState<string | null>(null);
    const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
    const [modal, setModal] = useState<'archive' | 'unarchive' | 'assign' | 'filter' | 'edit' | null>(null);

    const {
        data,
        isLoading,
        refetch,
        archived,
        setArchived,
        setPage,
        queryInputVal,
        exactDate,
        tags,
        clearFilter,
        clearAll,
        pagination,
        sortDirection,
        sort,
        filters,
        handleFiltersChange,
        handleTableChange,
        handleExactDateChange,
        handleSearch,
        handleClearSearch,
    } = useJobsFilters((params) => ({
        ...params,
        customerId,
    }));

    const closeModal = () => setModal(null);

    const handleArchiveJob = async () => {
        if (!jobIdToArchive) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        showGlobalOverlay({ type: 'LoadingOverlay' });
        try {
            await archiveJob(jobIdToArchive);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Archived',
                description: 'Successfully archived job',
            });
            await refetch();
            if (customerId) getJobsCount(customerId);
        } catch {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not archive job',
            });
        } finally {
            hideGlobalOverlay();
            setJobIdToArchive(null);
            closeModal();
        }
    };

    const handleUnarchiveJob = async () => {
        if (!jobIdToArchive) {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'No job selected',
            });
            return;
        }
        showGlobalOverlay({ type: 'LoadingOverlay' });
        try {
            await unarchiveJob(jobIdToArchive);
            Notification({
                type: 'success',
                placement: 'top',
                messageTitle: 'Job Unarchived',
                description: 'Successfully unarchived job',
            });
            await refetch();
            if (customerId) getJobsCount(customerId);
        } catch {
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: 'Could not unarchive job',
            });
        } finally {
            hideGlobalOverlay();
            setJobIdToArchive(null);
            closeModal();
        }
    };

    // Refresh the resources in memory
    useEffect(() => {
        listIndividuals({ size: 200 });
    }, [listIndividuals]);

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Row justify="space-between">
                    <Space size={8}>
                        <SearchBar
                            inputPlaceholder="Search jobs"
                            value={queryInputVal}
                            onSearch={handleSearch}
                            onClear={handleClearSearch}
                        />
                        <Button onClick={() => setModal('filter')}>Filter</Button>
                    </Space>

                    <Space size={8}>
                        <ArchiveFilter
                            value={archived ? 'archived' : 'active'}
                            onChange={(val) => {
                                setArchived(val === 'active' ? undefined : true);
                                setPage(0);
                            }}
                        />

                        <div>
                            <DatePicker
                                value={exactDate ? moment(exactDate, 'YYYY-MM-DD') : undefined}
                                onChange={handleExactDateChange}
                            />
                        </div>
                    </Space>
                </Row>

                <Row>
                    <AppliedJobFilters tags={tags} clearFilter={clearFilter} clearAll={clearAll} />
                </Row>

                <JobsTable
                    actions={{
                        archive: (job) => {
                            setJobIdToArchive(job.id);
                            setModal('archive');
                        },
                        unarchive: (job) => {
                            setJobIdToArchive(job.id);
                            setModal('unarchive');
                        },
                        ...(!viewOnly && {
                            assign: (job) => {
                                setSelectedJob(job);
                                setModal('assign');
                            },
                            edit: (job) => {
                                setSelectedJob(job);
                                setModal('edit');
                            },
                        }),
                    }}
                    loading={isLoading}
                    jobs={data?.list ?? []}
                    onRowClick={(record: JobResponse) => {
                        const isUnderManifest = Boolean(record.manifest?.id);
                        const to = isUnderManifest
                            ? `/manifests/${record.manifest?.id}/${record.id}`
                            : `/jobs/${record.id}`;
                        navigate(to);
                    }}
                    onChange={handleTableChange}
                    pagination={pagination}
                    currentSortDirection={sortDirection}
                    currentSortParam={sort}
                    optionalColumns={{
                        assignedTo: true,
                    }}
                />
            </Space>

            {selectedJob && (
                <AssignJobModal
                    jobData={{
                        jobId: selectedJob.id,
                        teamId: selectedJob.teamId,
                    }}
                    open={modal === 'assign'}
                    onSuccess={() => {
                        refetch();
                        closeModal();
                    }}
                    onClose={closeModal}
                />
            )}

            <ArchiveJobModal onArchive={handleArchiveJob} open={modal === 'archive'} onClose={closeModal} />
            <UnarchiveJobModal onUnarchive={handleUnarchiveJob} open={modal === 'unarchive'} onClose={closeModal} />
            {selectedJob && (
                <EditJobModal
                    job={selectedJob}
                    open={modal === 'edit'}
                    onClose={() => {
                        setSelectedJob(null);
                        closeModal();
                    }}
                    onSuccess={refetch}
                />
            )}

            <JobFiltersModal
                open={modal === 'filter'}
                onClose={closeModal}
                onClearAll={clearAll}
                onClearFilter={clearFilter}
                optionalFilters={{
                    assignedTo: true,
                }}
                filters={filters}
                onFiltersChange={handleFiltersChange}
            />
        </>
    );
};

export default JobsTab;
